import React, { useState, useCallback, useEffect } from 'react';
import './scss/_discovery-button.scss';

const SESSION_KEY = 'discovery_btn_collapsed';

interface Props {
  show: boolean;
  label: string;
  loadingLabel: string;
  fetchAndNavigate: () => Promise<void>;
}

const DiscoveryButton: React.FC<Props> = ({ show, label, loadingLabel, fetchAndNavigate }) => {
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1'
  );

  // Add bottom clearance to the page so fixed pill doesn't overlap in-flow buttons.
  useEffect(() => {
    const active = show && !collapsed;
    document.body.classList.toggle('discovery-pill-open', active);
    return () => document.body.classList.remove('discovery-pill-open');
  }, [show, collapsed]);

  const collapse = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(SESSION_KEY, '1');
    setCollapsed(true);
  }, []);

  const expand = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setCollapsed(false);
  }, []);

  const handleNavigate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await fetchAndNavigate();
    } catch {
      // network error — silently ignore
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className={`discovery-pill${collapsed ? ' discovery-pill--collapsed' : ''}`}>
        <button
          className={`discovery-pill__action${loading ? ' discovery-pill__action--loading' : ''}`}
          onClick={handleNavigate}
          disabled={loading}
        >
          {loading ? loadingLabel : label}
        </button>
        <button
          className="discovery-pill__dismiss"
          onClick={collapse}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>

      <button
        className={`discovery-tab${collapsed ? ' discovery-tab--visible' : ''}`}
        onClick={expand}
        aria-label="Expand discovery button"
      >
        ✦
      </button>
    </>
  );
};

export default DiscoveryButton;
